// src/hooks/useIdleTimeout.ts
import { useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';

interface UseIdleTimeoutOptions {
    /** Durasi timeout inaktivitas dalam milidetik */
    timeoutMs: number;
    /** Callback yang dipanggil ketika sesi habis */
    onTimeout: () => void;
    /** Apakah hook ini aktif */
    enabled?: boolean;
}

const PERINGATAN_SEBELUM_MS = 2 * 60 * 1000; // Peringatan 2 menit sebelum logout
const EVENTS_AKTIF = [
    'mousedown',
    'mousemove',
    'keydown',
    'scroll',
    'touchstart',
    'click',
    'wheel',
];

/**
 * Hook untuk mendeteksi inaktivitas pengguna dan melakukan logout otomatis.
 * Menampilkan toast peringatan 2 menit sebelum sesi habis.
 */
export function useIdleTimeout({
    timeoutMs,
    onTimeout,
    enabled = true,
}: UseIdleTimeoutOptions): void {
    const timerLogoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const timerPeringatanRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const toastIdRef = useRef<string | null>(null);
    const onTimeoutRef = useRef(onTimeout);

    // Selalu update ref supaya tidak perlu onTimeout di dependency
    useEffect(() => {
        onTimeoutRef.current = onTimeout;
    }, [onTimeout]);

    const resetTimer = useCallback(() => {
        // Bersihkan timer lama
        if (timerLogoutRef.current) clearTimeout(timerLogoutRef.current);
        if (timerPeringatanRef.current) clearTimeout(timerPeringatanRef.current);

        // Dismiss toast peringatan aktif jika ada
        if (toastIdRef.current) {
            toast.dismiss(toastIdRef.current);
            toastIdRef.current = null;
        }

        // Jangan set timer baru jika timeout tidak valid atau hook tidak aktif
        if (!enabled || timeoutMs <= 0) return;

        // Set peringatan 2 menit sebelum logout (jika durasi cukup panjang)
        const waktuPeringatan = timeoutMs - PERINGATAN_SEBELUM_MS;
        if (waktuPeringatan > 0) {
            timerPeringatanRef.current = setTimeout(() => {
                const id = toast(
                    '⚠️ Sesi Anda akan berakhir dalam 2 menit karena tidak ada aktivitas. Gerakkan mouse atau tekan tombol untuk memperbarui sesi.',
                    {
                        duration: PERINGATAN_SEBELUM_MS,
                        icon: '🔔',
                        style: {
                            background: '#fef3c7',
                            color: '#92400e',
                            border: '1px solid #f59e0b',
                        },
                    }
                );
                toastIdRef.current = id;
            }, waktuPeringatan);
        }

        // Set timer logout utama
        timerLogoutRef.current = setTimeout(() => {
            if (toastIdRef.current) {
                toast.dismiss(toastIdRef.current);
                toastIdRef.current = null;
            }
            toast.error('Sesi Anda telah berakhir karena tidak ada aktivitas. Silakan login kembali.', {
                duration: 5000,
            });
            onTimeoutRef.current();
        }, timeoutMs);
    }, [enabled, timeoutMs]);

    useEffect(() => {
        if (!enabled) return;

        // Set timer awal
        resetTimer();

        // Tambahkan listener untuk setiap event aktivitas
        EVENTS_AKTIF.forEach((event) => {
            window.addEventListener(event, resetTimer, { passive: true });
        });

        return () => {
            // Bersihkan saat unmount
            if (timerLogoutRef.current) clearTimeout(timerLogoutRef.current);
            if (timerPeringatanRef.current) clearTimeout(timerPeringatanRef.current);
            if (toastIdRef.current) toast.dismiss(toastIdRef.current);
            EVENTS_AKTIF.forEach((event) => {
                window.removeEventListener(event, resetTimer);
            });
        };
    }, [enabled, resetTimer]);
}
